import React, { createContext, useState, useContext, useEffect } from 'react';
import { IAuthContext, ILoginCredentials, ILoginResult, IInstitutionResult, IUser } from '../interfaces/auth.interface';
import { AuthService } from '../services/auth.service';
import InstitutionSelectionModal from '../components/institutions/InstitutionSelectionModal';
import axios from 'axios';

const AuthContext = createContext<IAuthContext & {
  showInstitutionModal: boolean;
  selectedInstitutionId: string | null;
  selectInstitution: (institutionId: string) => Promise<IInstitutionResult>;
  isImpersonating: boolean;
  impersonate: (userId: string) => Promise<void>;
  exitImpersonation: () => void;
}>({} as IAuthContext & {
  showInstitutionModal: boolean;
  selectedInstitutionId: string | null;
  selectInstitution: (institutionId: string) => Promise<IInstitutionResult>;
  isImpersonating: boolean;
  impersonate: (userId: string) => Promise<void>;
  exitImpersonation: () => void;
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<IUser | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [showInstitutionModal, setShowInstitutionModal] = useState(false);
    const [selectedInstitutionId, setSelectedInstitutionId] = useState<string | null>(
        AuthService.getSelectedInstitution()
    );
    const [isImpersonating, setIsImpersonating] = useState(AuthService.isImpersonating());

    useEffect(() => {
        // Check token validity on mount
        const token = localStorage.getItem('token');
        if (token && AuthService.isValidToken()) {
            // Set up axios header
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            axios.defaults.headers.common['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const currentUser = AuthService.getCurrentUser();
            if (currentUser) {
                setUser(currentUser);
                setIsAuthenticated(true);

                // Check if the user needs to select an institution
                if (currentUser.institutionIds && AuthService.needsInstitutionSelection(currentUser.institutionIds)) {
                    setShowInstitutionModal(true);
                }

                // Bootstrap the cached institution code for sessions that existed before
                // the code-caching change shipped (they have an ID but no cached code).
                const existingInstitutionId = AuthService.getSelectedInstitution();
                if (existingInstitutionId && !AuthService.getSelectedInstitutionCode()) {
                    void AuthService.cacheSelectedInstitutionCode(existingInstitutionId);
                }
            }
        } else {
            setUser(null);
            setIsAuthenticated(false);
            delete axios.defaults.headers.common['Authorization'];
        }
    }, []);

    const applyAuthenticatedUser = () => {
        if (AuthService.isValidToken()) {
            const currentUser = AuthService.getCurrentUser();
            setUser(currentUser);
            setIsAuthenticated(true);
            return currentUser;
        }
        return null;
    };

    const login = async (credentials: ILoginCredentials): Promise<ILoginResult> => {
        try {
            const response = await AuthService.login(credentials);

            if (response.status === 'OTP_REQUIRED' || response.status === 'ENROLLMENT_REQUIRED') {
                return {
                    success: false,
                    requiresTwoFactor: response.status === 'OTP_REQUIRED' ? 'OTP' : 'ENROLLMENT',
                    challengeId: response.challengeId,
                    qrDataUri: response.qrDataUri,
                    secret: response.secret,
                };
            }

            const currentUser = response.status === 'AUTHENTICATED' ? applyAuthenticatedUser() : null;
            if (currentUser) {
                if (currentUser.institutionIds && AuthService.needsInstitutionSelection(currentUser.institutionIds)) {
                    setShowInstitutionModal(true);
                } else if (currentUser.institutionIds && currentUser.institutionIds.length === 1) {
                    // Auto-select the only institution
                    const result = await selectInstitution(currentUser.institutionIds[0]);
                    if (!result.success) {
                        throw new Error(result.error);
                    }
                }
                return { success: true };
            }
            return { success: false };
        } catch (error) {
            if (error instanceof Error) {
                return { success: false, error: error.message };
            }
            return { success: false, error: 'An unknown error occurred' };
        }
    };

    const completeTwoFactorLogin = async (challengeId: string, code: string, rememberDevice: boolean): Promise<ILoginResult & { backupCodes?: string[] }> => {
        try {
            const response = await AuthService.verifyTwoFactor(challengeId, code, rememberDevice);
            const currentUser = response.status === 'AUTHENTICATED' ? applyAuthenticatedUser() : null;
            if (currentUser) {
                if (currentUser.institutionIds && AuthService.needsInstitutionSelection(currentUser.institutionIds)) {
                    setShowInstitutionModal(true);
                } else if (currentUser.institutionIds && currentUser.institutionIds.length === 1) {
                    const result = await selectInstitution(currentUser.institutionIds[0]);
                    if (!result.success) {
                        throw new Error(result.error);
                    }
                }
                return { success: true, backupCodes: response.backupCodes };
            }
            return { success: false };
        } catch (error) {
            if (error instanceof Error) {
                return { success: false, error: error.message };
            }
            return { success: false, error: 'An unknown error occurred' };
        }
    };

    const selectInstitution = async (institutionId: string) => {
        const result = await AuthService.setSelectedInstitution(institutionId);
        if (result.success) {
            setSelectedInstitutionId(institutionId);
            setShowInstitutionModal(false);
        }
        return result;
    };

    const logout = () => {
        AuthService.logout();
        setUser(null);
        setIsAuthenticated(false);
        setSelectedInstitutionId(null);
        setIsImpersonating(false);
    };

    const impersonate = async (userId: string) => {
        await AuthService.impersonate(userId);
        const impersonatedUser = AuthService.getCurrentUser();
        setUser(impersonatedUser);
        setIsImpersonating(true);
        // Auto-select first institution of impersonated user
        if (impersonatedUser?.institutionIds && impersonatedUser.institutionIds.length > 0) {
            const impersonatedInstitutionId = impersonatedUser.institutionIds[0];
            setSelectedInstitutionId(impersonatedInstitutionId);
            localStorage.setItem('selectedInstitutionId', impersonatedInstitutionId);
            await AuthService.cacheSelectedInstitutionCode(impersonatedInstitutionId);
        }
    };

    const exitImpersonation = () => {
        AuthService.exitImpersonation();
        const originalUser = AuthService.getCurrentUser();
        setUser(originalUser);
        setIsImpersonating(false);
        setSelectedInstitutionId(null);
    };

    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated,
            login,
            completeTwoFactorLogin,
            logout,
            showInstitutionModal,
            selectedInstitutionId,
            selectInstitution,
            isImpersonating,
            impersonate,
            exitImpersonation,
        }}>
            {showInstitutionModal && user?.institutionIds && (
                <InstitutionSelectionModal
                    open={showInstitutionModal}
                    institutionIds={user.institutionIds}
                    onSelect={selectInstitution}
                />
            )}
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
